import { useApi } from '../../hooks/useApi';
import { Async, EmptyState, PageHeader } from '../../components/ui/States';

interface UsageResponse {
  tenant: { id: string; name: string; slug: string; status: string };
  plan: {
    code: string;
    name: string;
    includedApiCalls: number;
    includedStreamGb: number;
    includedMonthlyActiveUsers: number;
  } | null;
  thisMonth: { apiCalls: number; playbackRequests: number };
  unmetered: string[];
  daily: {
    date: string;
    apiCalls: number;
    playbackRequests: number;
    streamGb: number;
    activeUsers: number;
  }[];
  hasData: boolean;
}

const NOT_MEASURED = 'Not measured yet';
const nf = new Intl.NumberFormat();

function Card({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="bg-[#120d1a] border border-[#241b33] rounded-xl p-5">
      <div className="text-xs font-medium uppercase tracking-wider text-gray-400">{label}</div>
      <div className="mt-3">{children}</div>
    </div>
  );
}

export function Usage() {
  const state = useApi<UsageResponse>('/admin/usage');

  return (
    <div>
      <PageHeader title="Usage" subtitle="How much your app has used Drameeka this month." />
      <Async state={state}>
        {(u) => {
          const limit = u.plan?.includedApiCalls;
          const unlimited = limit === -1;
          const pct =
            u.plan && !unlimited && limit && limit > 0
              ? Math.min(100, (u.thisMonth.apiCalls / limit) * 100)
              : null;
          const unmetered = new Set(u.unmetered);
          const maxCalls = Math.max(1, ...u.daily.map((d) => d.apiCalls));

          return (
            <div className="space-y-6">
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                <Card label="Your app">
                  <div className="text-lg font-bold text-white">{u.tenant.name}</div>
                  <div className="text-xs text-gray-400 mt-1">
                    Plan: {u.plan ? u.plan.name : 'No plan assigned yet'}
                  </div>
                </Card>
                <Card label="API calls this month">
                  <div className="text-2xl font-bold text-white">{nf.format(u.thisMonth.apiCalls)}</div>
                  {u.plan && (
                    <div className="mt-2">
                      {unlimited ? (
                        <p className="text-xs text-gray-400">Unlimited on your plan</p>
                      ) : pct !== null ? (
                        <>
                          <div
                            className="h-2 rounded-full bg-[#241b33] overflow-hidden"
                            role="progressbar"
                            aria-valuenow={Math.round(pct)}
                            aria-valuemin={0}
                            aria-valuemax={100}
                          >
                            <div
                              className={`h-full ${pct >= 90 ? 'bg-rose-500' : 'bg-violet-500'}`}
                              style={{ width: `${pct}%` }}
                            />
                          </div>
                          <p className="text-xs text-gray-400 mt-1">
                            {nf.format(u.thisMonth.apiCalls)} of {nf.format(limit as number)} included
                          </p>
                        </>
                      ) : null}
                    </div>
                  )}
                </Card>
                <Card label="Playback requests this month">
                  <div className="text-2xl font-bold text-white">
                    {nf.format(u.thisMonth.playbackRequests)}
                  </div>
                </Card>
                <Card label="Video streamed (GB)">
                  <div className="text-sm text-gray-400">{NOT_MEASURED}</div>
                </Card>
                <Card label="Active viewers">
                  <div className="text-sm text-gray-400">{NOT_MEASURED}</div>
                </Card>
              </div>

              {!u.hasData ? (
                <EmptyState
                  title="No traffic recorded yet"
                  message="Calls made with your API keys will show up here."
                />
              ) : (
                <div className="bg-[#120d1a] border border-[#241b33] rounded-xl overflow-x-auto">
                  <div className="px-4 py-3 border-b border-[#241b33] text-sm font-semibold text-white">
                    Last 30 days
                  </div>
                  <table className="w-full text-sm text-left">
                    <thead className="text-xs uppercase tracking-wider text-gray-400 border-b border-[#241b33]">
                      <tr>
                        <th className="px-4 py-2">Day</th>
                        <th className="px-4 py-2 w-1/3">API calls</th>
                        <th className="px-4 py-2">Playback requests</th>
                        <th className="px-4 py-2">Video streamed</th>
                        <th className="px-4 py-2">Active viewers</th>
                      </tr>
                    </thead>
                    <tbody>
                      {u.daily.map((d) => (
                        <tr key={d.date} className="border-b border-[#241b33] last:border-0">
                          <td className="px-4 py-2 text-gray-300 whitespace-nowrap">{d.date}</td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 rounded bg-[#241b33] overflow-hidden">
                                <div
                                  className="h-full bg-violet-500"
                                  style={{ width: `${(d.apiCalls / maxCalls) * 100}%` }}
                                />
                              </div>
                              <span className="text-gray-200 w-14 text-right">{nf.format(d.apiCalls)}</span>
                            </div>
                          </td>
                          <td className="px-4 py-2 text-gray-200">{nf.format(d.playbackRequests)}</td>
                          <td className="px-4 py-2 text-gray-400">
                            {unmetered.has('streamGb') ? NOT_MEASURED : `${nf.format(d.streamGb)} GB`}
                          </td>
                          <td className="px-4 py-2 text-gray-400">
                            {unmetered.has('activeUsers') ? NOT_MEASURED : nf.format(d.activeUsers)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          );
        }}
      </Async>
    </div>
  );
}
